//This file holds the recipes array used by the recipes section and 
//the methods that act upon it.

import {v4 as uuidv4} from 'uuid'  //Note renamed import
import moment from 'moment';

//This file holds the recipes array and all functions that act 
//upon it.

//The recipes array.
//NOTE: Due to the nature of web programming, each webpage has its 
//own copy this array. The copies must be kept consistant by the 
//storage callback functions.
let recipes;

//Function to get all saved recipes.
export const getRecipes = () => recipes;

//Function to load recipes and keep storage consistant.
export const loadRecipes = () => {
    //Get stored recipes an JSON.
    const recipesJSON = localStorage.getItem('recipes');

    //Try to parse recipes.
    try {
        recipes = recipesJSON ? JSON.parse(recipesJSON) : [];
    } catch (e) {
        recipes = [];
    }
}

//Function to save recipes. (NOT exported.)
const saveRecipes = () => {
    localStorage.setItem('recipes', JSON.stringify(recipes));
}

//Function to create a new recipe and return its ID.
export const createRecipe = () => {
    //Create new recipe object.
    const timestamp = moment().valueOf();
    const id = uuidv4();
    const newRecipe = {
        id: id,
        name: '',
        ingredients: [],    //To be filled with objects:
                            //{id, name, isOnHand}
        steps: [],          //To be filled with strings.
        createdAt: timestamp,
        updatedAt: timestamp
    };

    //Add new recipe to array.
    recipes.push(newRecipe);

    //Save updated array.
    saveRecipes();

    //Return the recipe's id.
    return id;
}

//Function to remove the recipe with the given unique ID.
export const removeRecipe = (id) => {
    const index = recipes.findIndex((recipe) => recipe.id === id);

    if (index > -1) {
        //A recipe to remove has been found.
        recipes.splice(index, 1);
        saveRecipes();
    }
}

//Function to get the name of the given recipe as seen by the 
//user.
export const getShownName = (recipe) => {
    if (recipe.name.trim()) {
        return recipe.name.trim();
    } else {
        return 'Unnamed Recipe';
    }
} 

//Function to sort the recipe list.
export const sortRecipes = (sortBy) => {
    //Return Object.
    let sortedrecipes;

    //Sort recipes based on parameter.
    if (sortBy === 'byEdited') {
        sortedrecipes = recipes.sort((a, b) => {
            if (a.updatedAt > b.updatedAt) {
                return -1;
            } else if (b.updatedAt > a.updatedAt) {
                return 1;
            } else {
                return 0;
            }
        });
    } else if (sortBy === 'byCreated') {
        sortedrecipes = recipes.sort((a, b) => {
            if (a.createdAt > b.createdAt) {
                return -1;
            } else if (b.createdAt > a.createdAt) {
                return 1;
            } else {
                return 0;
            }
        });
    } else { //alphabetical
        sortedrecipes = recipes.sort((a, b) => {
            if (getShownName(a).toLowerCase() 
                < getShownName(b).toLowerCase()) {
                return -1;
            } else if (getShownName(b).toLowerCase() 
                < getShownName(a).toLowerCase()) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    //Return sorted list.
    return sortedrecipes;
}

//Function to update a the name of a recipe by the recipe's ID. 
//The second parameter is the new title.  This function returns 
//undefined if the update fails or the updated recipe otherwise.
export const updateRecipeName = (id, newName) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === id);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Update recipe name and updatedAt timestamp.
    foundRecipe.name = newName;
    foundRecipe.updatedAt = moment().valueOf();
        
    //Save updated array.
    saveRecipes();

    //Return updated recipe.
    return foundRecipe;
}

//Function to add a new (empty) step to the recipe with the given
//id at the given index. This function returns undefined if the 
//update fails or the updated recipe otherwise.
//NOTE: The index count starts at zero.
export const addStep = (recipeId, index) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Add new step to recipe.
    foundRecipe.steps.splice(index, 0, '');

    //Change updatedAt timestamp and save array.
    foundRecipe.updatedAt = moment().valueOf();
    saveRecipes();

    //Return updated recipe.
    return foundRecipe;
}

//Function to update a step of the recipe with the given id. The
//step at the given index will be updated to the given text. This 
//function returns undefined if the update fails or the updated 
//recipe otherwise.
//NOTE: The index count starts at zero.
export const updateStep = (recipeId, index, newText) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Update recipe step.
    foundRecipe.steps.splice(index, 1, newText);

    //Change updatedAt timestamp and save array.
    foundRecipe.updatedAt = moment().valueOf();
    saveRecipes();

    //Return updated recipe.
    return foundRecipe;
}

//Function to remove a step from the recipe with the given id. The
//step at the given index will be removed. This function returns 
//undefined if the update fails or the updated recipe otherwise.
//NOTE: The index count starts at zero.
export const removeStep = (recipeId, index) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Delete step from recipe.
    foundRecipe.steps.splice(index, 1);

    //Change updatedAt timestamp and save array.
    foundRecipe.updatedAt = moment().valueOf();
    saveRecipes();

    //Return updated recipe.
    return foundRecipe;
}

//Function to get an array of the steps in the recipe with the
//given id. This function returns undefined if the retrieval fails.
export const getSteps = (recipeId) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Return the array of steps.
    return foundRecipe.steps;
}

//Function to add a new ingredient with the given name to the
//recipe with the given id. This function returns undefined if the 
//update fails or the updated recipe otherwise.
export const addIngredient = (recipeId, name) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Add new ingredient to the array of the found recipe.
    foundRecipe.ingredients.push({
        id: uuidv4(),
        name,
        isOnHand: false
    });

    //Change updatedAt timestamp.
    foundRecipe.updatedAt = moment().valueOf();

    //Update local storage.
    saveRecipes();

    //Return updated recipe.
    return foundRecipe;
}

//Function toggle the isOnHand fied an ingredient object by the 
//ingredient's unique ID field and that of its recipe. This 
//function returns undefined if the update fails or the updated 
//recipe otherwise.
export const toggleIngredient = (recipeId, ingredientId) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Look for ingredient.
    const foundIngredient 
        = foundRecipe.ingredients.find((ingredient) => {
            return ingredient.id === ingredientId;
        });

    //Check if ingredient was found.
    if (foundIngredient) {
        //An ingredient has been found.
        foundIngredient.isOnHand = !foundIngredient.isOnHand;

        //Update local storage.
        saveRecipes();

        //Return updated recipe.
        return foundRecipe;
    } else {
        //The recipe was unsuccessful.
        return undefined;
    }   
}

//Function delete an ingredient by its unique ID field and that of
//its recipe. This function returns undefined if the update fails 
//or the updated recipe otherwise.
export const removeIngredient = (recipeId, ingredientId) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Look for ingredient by its array index.
    const index 
        = foundRecipe.ingredients.findIndex((ingredient) => {
            return ingredient.id === ingredientId;
        });

    //Check if ingredient was found.
    if (index > -1) {
        //An ingredient has been found.
        foundRecipe.ingredients.splice(index, 1);

        //Change updatedAt timestamp.
        foundRecipe.updatedAt = moment().valueOf();

        //Update local storage.
        saveRecipes();

        //Return updated recipe.
        return foundRecipe;
    } else {
        //The recipe was unsuccessful.
        return undefined;
    }   
}

//Function to get an array of the ingredients in the recipe with
//the given id. This function returns undefined if the retrieval 
//fails.
export const getIngredients = (recipeId) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return undefined;
    }

    //Return the array of ingredients.
    return foundRecipe.ingredients;
}


//Function to return a summary of the status of the ingredients
//needed for a given recipe given the recipe's ID.
export const getIngredientStatusSummary = (recipeId) => {
    //Find the recipe to update.
    const foundRecipe 
        = recipes.find((recipe) => recipe.id === recipeId);

    //Abort if there was no recipe found.
    if (!foundRecipe) {
        return 'No recipe found.';
    }

    //Check if the recipe has any ingredients.
    if (!foundRecipe.ingredients.length) {
        return 'This recipe has no ingredients.';
    }

    //Check if all of the ingredients are on hand.
    if (foundRecipe.ingredients.every((ingredient) =>
        ingredient.isOnHand)) {
            return 'You have all of the ingredients.';
    }

    //Check if none of the ingredients are on hand.
    if (foundRecipe.ingredients.every((ingredient) =>
        !ingredient.isOnHand)) {
            return 'You have none of the ingredients.';
    }

    //If code gets here return last option.
    return 'You have some of the ingredients.';
}